import Cookies from 'js-cookie';
import { io, Socket } from 'socket.io-client';

export interface ScrapingRequest {
  url: string;
  code: string;
  categoryId?: number;
  subCategoryId?: number;
}

export interface ScrapingResponse {
  success: boolean;
  data?: unknown;
  message?: string;
  jobId?: string;
}

export interface ScrapingJob {
  id: string;
  url: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  result?: unknown;
  error?: string;
  createdAt: string;
  completedAt?: string;
}

// Add proper types for event data
export interface ScrapingProgressData {
  jobId: string;
  progress: number;
  message?: string;
}

export interface ScrapingCompletedData {
  jobId: string;
  success: boolean;
  data?: unknown;
  message?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

export interface ScrapingErrorData {
  jobId: string;
  message: string;
  error?: unknown;
}

// Generic event callback type
type EventCallback<T = unknown> = (data: T) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private baseUrl = 'wss://admin-backend.findecor.io'; // Remove trailing space and slash
  private isConnected = false;
  private listeners: Map<string, EventCallback[]> = new Map();

  private getAuthToken(): string | undefined {
    return Cookies.get('authToken');
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const token = this.getAuthToken();
        if (!token) {
          reject(new Error('No authentication token found'));
          return;
        }

        console.log('🔌 Attempting to connect to:', this.baseUrl);
        console.log('🔑 Using token:', token.substring(0, 20) + '...');

        this.socket = io('wss://admin-backend.findecor.io/product', {
          transports: ['websocket'],
          auth: {
            token: `Bearer ${token}`,
          },
          extraHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });

        this.socket.on('connect', () => {
          console.log('🔌 WebSocket connected:', this.socket?.id);
          this.isConnected = true;
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          console.log('🔌 WebSocket disconnected:', reason);
          this.isConnected = false;
        });

        this.socket.on('connect_error', (error) => {
          console.error('❌ WebSocket connection error:', error);
          this.isConnected = false;
          reject(error);
        });

        // 🔐 Authentication
        this.socket.on('authenticated', (data) => {
          console.log('🔐 Authentication successful:', data);
          this.emit('authenticated', data);
        });

        this.socket.on('jobProgress', (data) => {
          console.log('📥 Job progress:', data);
          this.emit('jobProgress', data);
        });

        // ❌ Error listener
        this.socket.on('error', (data) => {
          console.error('❌ Socket error:', data);
          this.emit('error', data);
        });

        // 📡 Scraping events
        this.socket.on('scraping-progress', (data: ScrapingProgressData) => {
          console.log('📡 Scraping progress received:', data);
          this.emit('scraping-progress', data);
        });

        this.socket.on('scraping-completed', (data: ScrapingCompletedData) => {
          console.log('✅ Scraping completed:', data);
          this.emit('scraping-completed', data);
        });

        this.socket.on('scraping-error', (data: ScrapingErrorData) => {
          console.error('❌ Scraping error:', data);
          this.emit('scraping-error', data);
        });

        // 🔍 Job related events
        this.socket.on('scrape-job-details', (data) => {
          console.log('📋 Scrape job details:', data);
          this.emit('scrape-job-details', data);
        });

        this.socket.on('scrape-job-stopped', (data) => {
          console.log('⏹️ Scrape job stopped:', data);
          this.emit('scrape-job-stopped', data);
        });

        this.socket.on('scraping-jobs-list', (data) => {
          console.log('📋 Scraping jobs list:', data);
          this.emit('scraping-jobs-list', data);
        });

        // 🟢 Response to scrapeProducts - listen for scrape-started event
        this.socket.on('scrape-started', (data: ScrapingResponse) => {
          console.log('📥 Scrape started response:', data);
          this.emit('scraping-response', data);
        });

        // Also listen for scrapeProducts response (fallback)
        this.socket.on('scrapeProducts', (data: ScrapingResponse) => {
          console.log('📥 ScrapeProducts response:', data);
          this.emit('scraping-response', data);
        });

        this.socket.on('importProducts', (data: ScrapingResponse) => {
          console.log('📥 ImportProducts response:', data);
          this.emit('import-products-response', data);
        });
      } catch (error) {
        console.error('💥 Failed to initialize WebSocket:', error);
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting WebSocket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  // Start product scraping
  startScraping(request: ScrapingRequest): Promise<ScrapingResponse> {
    return new Promise((resolve, reject) => {
      if (!this.isSocketConnected()) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      console.log('🚀 Starting scraping request:', request);

      const timeout = setTimeout(() => {
        reject(new Error('Scraping request timeout'));
      }, 30000); // 30 seconds timeout

      // Listen for the response
      const responseHandler: EventCallback<ScrapingResponse> = (data) => {
        clearTimeout(timeout);
        this.off('scraping-response', responseHandler);
        resolve(data);
      };

      this.on('scraping-response', responseHandler);

      // Send scraping request
      this.socket?.emit('scrapeProducts', request);
    });
  }

  // Get scraping jobs list
  getScrapingJobs(): Promise<ScrapingJob[]> {
    return new Promise((resolve, reject) => {
      if (!this.isSocketConnected()) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      console.log('📋 Getting scraping jobs...');

      const timeout = setTimeout(() => {
        reject(new Error('Get jobs request timeout'));
      }, 10000);

      const responseHandler: EventCallback<{
        success: boolean;
        data: ScrapingJob[];
      }> = (data) => {
        clearTimeout(timeout);
        this.off('scraping-jobs-list', responseHandler);
        if (data.success) {
          resolve(data.data);
        } else {
          reject(new Error('Failed to get scraping jobs'));
        }
      };

      this.on('scraping-jobs-list', responseHandler);

      this.socket?.emit(
        'get-scrape-jobs',
        {},
        (response: { success: boolean; data: ScrapingJob[] }) => {
          console.log('📋 Jobs list response:', response);
          clearTimeout(timeout);
          this.off('scraping-jobs-list', responseHandler);
          if (response.success) {
            resolve(response.data);
          } else {
            reject(new Error('Failed to get scraping jobs'));
          }
        }
      );
    });
  }

  // Get specific scraping job
  getScrapingJob(jobId: string): Promise<ScrapingJob> {
    return new Promise((resolve, reject) => {
      if (!this.isSocketConnected()) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      console.log('🔍 Getting scraping job:', jobId);

      const timeout = setTimeout(() => {
        reject(new Error('Get job request timeout'));
      }, 10000);

      const responseHandler: EventCallback<{
        success: boolean;
        data: ScrapingJob;
      }> = (data) => {
        clearTimeout(timeout);
        this.off('scraping-job-details', responseHandler);
        if (data.success) {
          resolve(data.data);
        } else {
          reject(new Error('Failed to get scraping job'));
        }
      };

      this.on('scraping-job-details', responseHandler);

      this.socket?.emit(
        'get-scrape-job',
        { jobId },
        (response: { success: boolean; data: ScrapingJob }) => {
          console.log('📥 Job details response:', response);
          clearTimeout(timeout);
          this.off('scraping-job-details', responseHandler);
          if (response.success) {
            resolve(response.data);
          } else {
            reject(new Error('Failed to get scraping job'));
          }
        }
      );
    });
  }

  // Stop scraping job
  stopScrapingJob(jobId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.isSocketConnected()) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      console.log('⏹️ Stopping scraping job:', jobId);

      const timeout = setTimeout(() => {
        reject(new Error('Stop job request timeout'));
      }, 10000);

      this.socket?.emit(
        'stop-scrape-job',
        { jobId },
        (response: { success: boolean }) => {
          console.log('🛑 Stop job response:', response);
          clearTimeout(timeout);
          if (response.success) {
            resolve(true);
          } else {
            reject(new Error('Failed to stop scraping job'));
          }
        }
      );
    });
  }

  // Event listener methods
  on<T = unknown>(event: string, callback: EventCallback<T>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback as EventCallback);
  }

  off<T = unknown>(event: string, callback: EventCallback<T>): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback as EventCallback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  private emit(event: string, data: unknown): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => callback(data));
    }
  }

  // Helper method to upload file to server with progress tracking
  private uploadFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);

      xhr.open('POST', `${import.meta.env.VITE_BASE_URL}/upload`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${this.getAuthToken()}`);

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data.url);
          } catch {
            reject(new Error('Failed to parse server response'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.message || 'Failed to upload file'));
          } catch {
            reject(new Error('Failed to upload file'));
          }
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error occurred while uploading file'));
      };

      xhr.send(formData);
    });
  }

  // Import products from Excel and images ZIP with progress tracking
  async importProducts(
    excelFile: File,
    imagesZipFile: File,
    onProgress?: (type: 'excel' | 'zip', progress: number) => void
  ): Promise<ScrapingResponse> {
    return new Promise((resolve, reject) => {
      if (!this.isSocketConnected()) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      console.log('📤 Starting import process...');

      const timeout = setTimeout(() => {
        reject(new Error('Import request timed out after 5 minutes'));
      }, 300000); // 5 minutes timeout

      try {
        // 1. Upload Excel file with progress tracking
        console.log('📤 Uploading Excel file...');
        const excelUrl = this.uploadFile(excelFile, (progress) => {
          console.log(`Excel upload progress: ${progress}%`);
          onProgress?.('excel', progress);
        });
        console.log('✅ Excel file uploaded successfully:', excelUrl);

        // 2. Upload ZIP file with progress tracking
        console.log('📤 Uploading images ZIP file...');
        const imagesZipUrl = this.uploadFile(imagesZipFile, (progress) => {
          console.log(`ZIP upload progress: ${progress}%`);
          onProgress?.('zip', progress);
        });
        console.log('✅ ZIP file uploaded successfully:', imagesZipUrl);

        // 3. Set up response handler
        const responseHandler = (response: ScrapingResponse) => {
          clearTimeout(timeout);
          this.off('import-products-response', responseHandler);

          console.log('📥 Received import response:', response);

          if (response && (response.success || response.data)) {
            resolve({
              success: true,
              message: response.message || 'Import started successfully',
              data: response.data,
            });
          } else {
            reject(new Error(response?.message || 'Import failed'));
          }
        };

        // 4. Listen for the response
        this.on('import-products-response', responseHandler);

        // 5. Emit the import request
        console.log('📡 Sending import request...');
        this.socket?.emit('importProducts', {
          excelUrl,
          imagesZipUrl, // This is now guaranteed to be defined
        });
      } catch (error) {
        clearTimeout(timeout);
        console.error('❌ Import process failed:', error);
        reject(
          error instanceof Error ? error : new Error('Import process failed')
        );
      }
    });
  }
}

// Create singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
